import React, {useState} from 'react'
import {StyledMarkerButton} from '../TimeSeriesDashboardStyle'
import {Fade, Menu, MenuItem, MenuList, styled} from '@mui/material'
import Tooltip, {tooltipClasses} from '@mui/material/Tooltip'
import {Add, Delete, Edit} from '@mui/icons-material'
import ClearAllDialog from '../charts/MarkerTooltip/ClearAllDialog'
import {handleClickAddMarker, handleClickEditMarker, updateMouseListners} from './markerHelpers'
import {useChartMarker} from '../../store/hooks/chartMarker'
import PropTypes from 'prop-types'
import {updateLabelsAlignments} from '../charts/plugins/marker'
import {useToggleModeEnabled} from '../hooks'

const StyledMenu = styled(Menu)(({theme}) => ({
  '& .MuiPaper-root': {
    padding: theme.spacing(0),
    borderRadius: 4,
    marginLeft: theme.spacing(1),

    '& .MuiMenuItem-root': {
      color: theme.palette.hpPrimaryDark,
      fontWeight: theme.typography.fontWeightSemibold,
      padding: theme.spacing(0, 1.5),
      '& .MuiSvgIcon-root': {
        fontSize: 20,
        fontWeight: theme.typography.fontWeightSemibold,

        marginRight: theme.spacing(1)
      },

      '&.Mui-disabled': {
        color: theme.palette.hpBase
      }
    }
  }
}))
const StyledTooltip = styled(({className, ...props}) => <Tooltip {...props} classes={{popper: className}} />)(
  ({theme}) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.hpBaseDarker,
      maxWidth: 160,
      fontSize: theme.typography.subtitle1.fontSize,
      lineHeight: '20px',
      padding: theme.spacing(1)
    }
  })
)

const MarkerMenu = ({chartRef, displayLegend, setClickAwayEnabled}) => {
  const topPos =
    chartRef?.current?.canvas.getBoundingClientRect().top + chartRef?.current?.options.layout.padding.top - 22 // calculated as relative to window to set dialog center of chart
  const [anchorEl, setAnchorEl] = useState(null)
  const [isAnyModeEnabled, setIsAnyModeEnabled] = useState(false)
  const open = Boolean(anchorEl)

  const {updateMarker} = useChartMarker()

  const handleClickOpenMenu = (event) => {
    event.stopPropagation()
    chartRef.current?.setTooltipVisible(false)
    updateMouseListners(chartRef.current)
    setAnchorEl(event.currentTarget)
  }
  const handleCloseMenu = () => {
    setAnchorEl(null)
  }
  const [dialogOpen, setDialogOpen] = React.useState(false)

  const handleClickDialogOpen = () => {
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
  }
  const clearAllMarkers = () => {
    if (chartRef.current) {
      chartRef.current.options.plugins.annotation.annotations = {}
      chartRef.current.marker.lineLabels = {}
      chartRef.current.marker.annotations = {}

      // Update Redux state via dispatch
      updateMarker(chartRef.current.marker.name, {})

      updateLabelsAlignments([], 0, chartRef.current)
      chartRef.current.update()
      handleCloseDialog()
      handleCloseMenu()
    }
  }

  useToggleModeEnabled(chartRef, setIsAnyModeEnabled)
  return (
    <>
      <ClearAllDialog
        handleCloseDialog={handleCloseDialog}
        open={dialogOpen}
        clearAll={clearAllMarkers}
        topPos={topPos}
      />
      <StyledMarkerButton
        disabled={!displayLegend || chartRef?.current?.markerTooltipPosition != null}
        ismenuopen={open || isAnyModeEnabled}
        id='marker-button'
        aria-controls={open ? 'marker-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClickOpenMenu}
      >
        Event Markers
      </StyledMarkerButton>
      <StyledMenu
        onClick={(e) => e.stopPropagation()}
        elevation={3}
        id='marker-menu'
        MenuListProps={{
          'aria-labelledby': 'marker-button'
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left'
        }}
        TransitionComponent={Fade}
      >
        <MenuList dense sx={{paddingY: 0}}>
          <StyledTooltip
            placement='top-end'
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, -12]
                    }
                  }
                ]
              }
            }}
            title={
              chartRef.current && Object.keys(chartRef.current.options.plugins.annotation.annotations).length === 4
                ? 'You have reached the max number of markers'
                : null
            }
          >
            <span>
              <MenuItem
                disabled={
                  chartRef.current && Object.keys(chartRef.current.options.plugins.annotation.annotations).length === 4
                }
                selected={chartRef?.current?.options.plugins.marker.enabled}
                onClick={(e) => {
                  e.stopPropagation()
                  handleClickAddMarker(chartRef.current, setClickAwayEnabled)
                  handleCloseMenu()
                }}
              >
                <Add
                  sx={{
                    stroke: (theme) =>
                      chartRef.current &&
                      Object.keys(chartRef.current.options.plugins.annotation.annotations).length === 4
                        ? theme.palette.hpBase
                        : theme.palette.hpPrimaryDark
                  }}
                />
                Add Marker
              </MenuItem>
            </span>
          </StyledTooltip>
          <MenuItem
            disabled={
              chartRef.current && Object.keys(chartRef.current.options.plugins.annotation.annotations).length === 0
            }
            selected={
              chartRef.current &&
              Object.keys(chartRef.current.options.plugins.annotation.annotations).length !== 0 &&
              chartRef.current.options.plugins.marker.editEnabled
            }
            onClick={(e) => {
              e.stopPropagation()
              handleClickEditMarker(chartRef.current, setClickAwayEnabled)
              handleCloseMenu()
            }}
          >
            <Edit />
            Edit Marker
          </MenuItem>
          <MenuItem
            disabled={
              chartRef.current && Object.keys(chartRef.current.options.plugins.annotation.annotations).length === 0
            }
            onClick={(e) => {
              e.stopPropagation()
              handleClickDialogOpen()
            }}
          >
            <Delete />
            Clear All Markers
          </MenuItem>
        </MenuList>
      </StyledMenu>
    </>
  )
}

MarkerMenu.propTypes = {
  chartRef: PropTypes.object,
  displayLegend: PropTypes.bool,
  setClickAwayEnabled: PropTypes.func
}

export default MarkerMenu

/* Function Descriptions:
   MarkerMenu is a React component that provides a menu for managing event markers on a chart.
   It allows users to add, edit, and clear markers, with appropriate tooltips and validation for maximum marker limits.
   The menu is styled using Material-UI components and integrates with the chart's marker functionality.
  
   Functions:
   - handleClickOpenMenu: Opens the marker menu and updates mouse listeners.
   - handleCloseMenu: Closes the marker menu.
   - handleClickDialogOpen: Opens the "Clear All Markers" confirmation dialog.
   - handleCloseDialog: Closes the "Clear All Markers" confirmation dialog.
   - clearAllMarkers: Removes all markers and updates the chart.
  
   Props:
   @param {Object} chartRef - A reference to the chart instance.
   @param {boolean} displayLegend - Determines if the legend is displayed, enabling marker actions.
   @param {Function} setClickAwayEnabled - A function to toggle click-away behavior for the menu.
  
   State:
   @property {Object|null} anchorEl - The anchor element for the menu.
   @property {boolean} isAnyModeEnabled - Indicates if any marker mode (add/edit) is enabled.
   @property {boolean} dialogOpen - Controls the visibility of the "Clear All Markers" dialog.
  
   Functional Summary:
   - Opens and closes the marker menu.
   - Adds a new marker to the chart, with a maximum limit of 4 markers.
   - Edits an existing marker on the chart.
   - Clears all markers from the chart with a confirmation dialog.
   - Displays tooltips for menu items, including validation messages.
   - Integrates with the chart's marker and annotation plugins for seamless updates.
  
   Dependencies:
   - React hooks for state management.
   - Material-UI components for styling and tooltips.
   - Custom hooks and helper functions for marker management.
 */
